import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { fmtIDR, fmtTanggal, fmtWaktuEntry, todayLocal, formatRibuan, parseRibuan } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Pencil, Trash2, Loader2, Filter, Upload, CheckCircle2 } from "lucide-react";

const firstDayOfMonth = () => todayLocal().slice(0, 8) + "01";

export default function Laporan() {
  const { user } = useAuth();
  const [start, setStart] = useState(firstDayOfMonth());
  const [end, setEnd] = useState(todayLocal());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [editing, setEditing] = useState(null);
  const [editSaldo, setEditSaldo] = useState("");
  const [editKet, setEditKet] = useState("");
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/saldo", { params: { start, end } })
      .then((r) => setEntries(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [start, end]);

  useEffect(load, [load]);

  const download = async (kind) => {
    setExporting(kind);
    try {
      const res = await api.get(`/saldo/export/${kind}`, {
        params: { start, end },
        responseType: "blob",
      });
      const ext = kind === "excel" ? "xlsx" : "pdf";
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `posisi-saldo_${start}_sd_${end}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Laporan ${kind === "excel" ? "Excel" : "PDF"} berhasil diunduh`);
    } catch (e) {
      toast.error("Gagal mengunduh laporan");
    } finally {
      setExporting("");
    }
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setEditSaldo(formatRibuan(String(Math.round(entry.total_saldo))));
    setEditKet(entry.keterangan || "");
  };

  const saveEdit = async () => {
    const nilai = parseRibuan(editSaldo);
    if (nilai <= 0) {
      toast.error("Total saldo harus lebih dari 0");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/saldo/${editing.id}`, { total_saldo: nilai, keterangan: editKet });
      toast.success("Data saldo berhasil diperbarui");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Gagal memperbarui data"));
    } finally {
      setSaving(false);
    }
  };

  const hapus = async (entry) => {
    if (!window.confirm(`Hapus data saldo tanggal ${fmtTanggal(entry.tanggal)}?`)) return;
    try {
      await api.delete(`/saldo/${entry.id}`);
      toast.success("Data berhasil dihapus");
      load();
    } catch (e) {
      toast.error(apiError(e, "Gagal menghapus data"));
    }
  };

  const canEdit = (e) => isAdmin || e.created_by === user?.id;

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Pilih file Excel terlebih dahulu");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/saldo/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      if (data.imported > 0) load();
    } catch (e) {
      toast.error(apiError(e, "Gagal mengimpor file"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="laporan-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Laporan Posisi Saldo</h1>
        <p className="text-sm text-slate-500 mt-1">
          Data saldo setiap hari beserta waktu entry. Unduh dalam format Excel atau PDF.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <Filter className="h-4 w-4" /> Filter Rentang Tanggal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="start">Dari Tanggal</Label>
              <Input
                id="start"
                type="date"
                data-testid="filter-start-date-input"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end">Sampai Tanggal</Label>
              <Input
                id="end"
                type="date"
                data-testid="filter-end-date-input"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="h-10"
              />
            </div>
            <Button
              onClick={load}
              data-testid="filter-apply-button"
              className="h-10 bg-slate-900 text-white hover:bg-slate-800"
            >
              Terapkan
            </Button>
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                data-testid="import-excel-button"
                onClick={() => { setShowImport(true); setImportResult(null); setImportFile(null); }}
                className="h-10 border-slate-900 text-slate-900 hover:bg-slate-100"
              >
                <Upload className="mr-2 h-4 w-4" />
                Import Excel
              </Button>
              <Button
                variant="outline"
                data-testid="export-excel-button"
                disabled={exporting !== "" || entries.length === 0}
                onClick={() => download("excel")}
                className="h-10 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              >
                {exporting === "excel" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
                Excel
              </Button>
              <Button
                variant="outline"
                data-testid="export-pdf-button"
                disabled={exporting !== "" || entries.length === 0}
                onClick={() => download("pdf")}
                className="h-10 border-rose-600 text-rose-700 hover:bg-rose-50"
              >
                {exporting === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-daily-balance-table">
              <thead>
                <tr className="bg-slate-900 text-left text-white">
                  <th className="px-4 py-3 font-semibold">No</th>
                  <th className="px-4 py-3 font-semibold">Tanggal</th>
                  <th className="px-4 py-3 font-semibold">Hari</th>
                  <th className="px-4 py-3 font-semibold text-right">Total Saldo</th>
                  <th className="px-4 py-3 font-semibold">Keterangan</th>
                  <th className="px-4 py-3 font-semibold">Waktu Entry</th>
                  <th className="px-4 py-3 font-semibold">Oleh</th>
                  <th className="px-4 py-3 font-semibold text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500" data-testid="report-loading">
                      Memuat data...
                    </td>
                  </tr>
                )}
                {!loading && entries.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500" data-testid="report-empty-state">
                      Tidak ada data pada rentang tanggal ini.
                    </td>
                  </tr>
                )}
                {!loading &&
                  entries.map((e, i) => (
                    <tr
                      key={e.id}
                      className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}
                      data-testid={`report-row-${e.tanggal}`}
                    >
                      <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                      <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{fmtTanggal(e.tanggal)}</td>
                      <td className="px-4 py-3 text-slate-600">{e.hari}</td>
                      <td className="px-4 py-3 text-right font-num font-semibold text-slate-900 whitespace-nowrap">
                        {fmtIDR(e.total_saldo)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-[220px] truncate">{e.keterangan || "-"}</td>
                      <td className="px-4 py-3 font-num text-xs text-slate-600 whitespace-nowrap">
                        {fmtWaktuEntry(e.created_at)}
                        {e.updated_at && (
                          <span className="block text-amber-600" data-testid={`report-edited-flag-${e.tanggal}`}>
                            diedit: {fmtWaktuEntry(e.updated_at)} ({e.updated_by_nama})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{e.created_by_nama}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-1">
                          {canEdit(e) && (
                            <button
                              onClick={() => openEdit(e)}
                              data-testid={`report-edit-button-${e.tanggal}`}
                              className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-900 hover:text-white"
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => hapus(e)}
                              data-testid={`report-delete-button-${e.tanggal}`}
                              className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-rose-600 hover:text-white"
                              title="Hapus"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Import Data Historis dari Excel</DialogTitle>
            <DialogDescription>
              Mendukung format template "Posisi Saldo" (blok KWARTAL dengan kolom Tanggal / Hari /
              Total Saldo) maupun tabel sederhana berisi tanggal asli. Tanggal yang sudah ada akan
              dilewati, baris dengan saldo kosong diabaikan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="import-file">File Excel (.xlsx)</Label>
              <Input
                id="import-file"
                type="file"
                accept=".xlsx,.xlsm"
                data-testid="import-file-input"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              />
            </div>
            {importResult && (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm"
                data-testid="import-result-summary"
              >
                <p className="flex items-center gap-2 font-semibold text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" /> Import selesai
                </p>
                <ul className="mt-2 space-y-1 text-emerald-900">
                  <li>Berhasil diimport: <span className="font-num font-bold" data-testid="import-result-imported">{importResult.imported}</span></li>
                  <li>Dilewati (tanggal sudah ada): <span className="font-num font-bold" data-testid="import-result-duplicate">{importResult.duplicate}</span></li>
                  <li>Diabaikan (saldo kosong): <span className="font-num font-bold" data-testid="import-result-empty">{importResult.empty}</span></li>
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)} data-testid="import-close-button">
              Tutup
            </Button>
            <Button
              onClick={handleImport}
              disabled={importing || !importFile}
              data-testid="import-submit-button"
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Mulai Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-saldo-dialog">
          <DialogHeader>
            <DialogTitle>Edit Saldo — {editing ? fmtTanggal(editing.tanggal) : ""}</DialogTitle>
            <DialogDescription>Perubahan akan tercatat dengan waktu edit dan nama pengedit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-saldo">Total Saldo (Rp)</Label>
              <Input
                id="edit-saldo"
                inputMode="numeric"
                data-testid="edit-saldo-input"
                value={editSaldo}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 15);
                  setEditSaldo(digits ? formatRibuan(digits) : "");
                }}
                className="font-num"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-ket">Keterangan</Label>
              <Textarea
                id="edit-ket"
                data-testid="edit-keterangan-input"
                value={editKet}
                onChange={(e) => setEditKet(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-cancel-button">
              Batal
            </Button>
            <Button
              onClick={saveEdit}
              disabled={saving}
              data-testid="edit-save-button"
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Simpan Perubahan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
